import { formatHealAmount } from './combat-text';

/**
 * **A consolidação do `+VALOR` do Roubo de Vida** (gate V28G.I).
 *
 * ## O que esta função é, e o que ela deliberadamente não é
 *
 * Uma função pura sobre uma lista de factos que a autoridade confirmou. Não
 * conhece uma cena, um sprite, um pool, um tween nem o Phaser; não sabe o que é
 * uma joia, um perfil, um nível ou uma percentagem; e não pode inventar um
 * valor, porque o único que recebe já vem calculado.
 *
 * As duas cenas chamam-na e desenham o que ela devolver. Duas cópias da mesma
 * agregação seriam duas coisas que podem divergir, e a que divergisse seria a do
 * modo que ninguém abriu naquele dia.
 *
 * ## A chave é `(atacante, tique)`
 *
 * E não `(atacante, alvo, tique)`. O jogador quer saber **quanto a sua nave
 * recuperou**; de qual adversário cada fração saiu não muda decisão nenhuma, e
 * um número por alvo dividiria a resposta em pedaços que ele teria de somar de
 * cabeça.
 *
 * Tiques diferentes continuam a ser ocorrências diferentes: um tique novo é um
 * momento novo, e juntá-los seria inventar simultaneidade.
 *
 * ## Porque soma em MILLI inteiro
 *
 * Porque somar em pontos acumularia erro de vírgula flutuante: `0,1 + 0,1 + 0,1`
 * não é `0,3`. Em milli a soma é exata, e a divisão acontece uma vez, no
 * formatador. Nada é arredondado antes da soma.
 *
 * ## Porque a ordem de saída é a de CHEGADA
 *
 * Um `Map` preserva a ordem de inserção, portanto o primeiro número devolvido é
 * o do primeiro facto que chegou. Ordenar por tique seria impor uma ordem que a
 * fila já tinha, e reordenar factos autoritativos não é uma coisa que uma
 * apresentação deva fazer.
 */

/** Uma cura que a autoridade confirmou, como as duas cenas a recebem. */
export interface ConfirmedHeal {
  /** Quem foi curado. Metade da chave. */
  readonly attacker: string;
  /** O tique da SIMULAÇÃO em que aconteceu. A outra metade. */
  readonly tick: number;
  /** A vida REALMENTE acrescentada, em milli inteiro. Nunca recalculada. */
  readonly healedMilli: number;
}

/** Um número a desenhar: um por atacante e por tique. */
export interface HealNumber {
  readonly attacker: string;
  readonly tick: number;
  /** O total exato do tique, em milli. */
  readonly milli: number;
  /** Já formatado: `+1`, `+1,8`, `+0,09`. */
  readonly label: string;
}

/**
 * Agrupa, soma e formata.
 *
 * Devolve um número por `(atacante, tique)`. **Toda a cura positiva aparece**:
 * não há limiar e não há supressão - `formatHealAmount` escreve até ao milésimo
 * de ponto, que é a precisão em que a cura viaja.
 *
 * Um grupo cai apenas quando não houve cura nenhuma, o que a simulação já não
 * anuncia: ela não emite evento com a vida cheia, com o atacante em baixo, nem
 * quando a cura ficou toda no resto.
 */
export function healNumbersFrom(heals: Iterable<ConfirmedHeal>): readonly HealNumber[] {
  const totals = new Map<string, { attacker: string; tick: number; milli: number }>();
  for (const heal of heals) {
    if (!Number.isFinite(heal.healedMilli) || heal.healedMilli <= 0) {
      /*
       * Uma cura de zero não é um facto: a simulação não a anuncia, e aceitar
       * uma aqui seria abrir uma porta para um `+0` chegar por outro caminho.
       */
      continue;
    }
    const key = `${heal.tick} ${heal.attacker}`;
    const found = totals.get(key);
    if (found) {
      found.milli += heal.healedMilli;
      continue;
    }
    totals.set(key, { attacker: heal.attacker, tick: heal.tick, milli: heal.healedMilli });
  }
  const numbers: HealNumber[] = [];
  for (const total of totals.values()) {
    const label = formatHealAmount(total.milli);
    if (label === null) {
      continue;
    }
    numbers.push({ attacker: total.attacker, tick: total.tick, milli: total.milli, label });
  }
  return numbers;
}
